import * as amplitude from "@amplitude/analytics-browser";
import * as Sentry from "@sentry/react";

import { persistor, resetStore, store } from "@/store";
import { deleteCookie } from "./generalUtilities";

type IntercomGlobal = { Intercom?: (command: string) => void };

/**
 * Tears down every trace of the current user's session — Redux (in-memory +
 * persisted), auth cookies, localStorage, and third-party analytics — then
 * navigates to `redirectTo`.
 *
 * Intentionally performs NO API call so it can be reused on the dead-token
 * path (where contacting the backend would only fail). User-initiated logout
 * should call the backend first via `logoutUser` in `services/user`, then this.
 *
 * Every external call is guarded so a single failing integration never blocks
 * the user from logging out.
 */
export const clearUserSession = async (
  redirectTo: string = "/#/login",
): Promise<void> => {
  // Reset the in-memory Redux state immediately, then drop the persisted copy.
  try {
    store.dispatch(resetStore());
    await persistor.purge();
  } catch {
    // best-effort — proceed with the rest of the teardown regardless
  }

  // Auth token + any other local session flags.
  localStorage.removeItem("sootchy_user_session");
  deleteCookie("loginRes"); // legacy — safe to call even if cookie is absent
  try {
    localStorage.clear();
  } catch {
    // ignore storage access errors (e.g. blocked cookies)
  }

  // Third-party session state — each isolated so one failure can't block logout.
  try {
    amplitude.reset();
  } catch {
    // ignore
  }
  try {
    Sentry.setUser(null);
  } catch {
    // ignore
  }
  try {
    (window as unknown as IntercomGlobal).Intercom?.("shutdown");
  } catch {
    // ignore
  }

  // Navigate to the post-logout destination, then force a hard reload. The
  // reload is NOT redundant: this is a HashRouter app, so changing the URL to a
  // hash route (e.g. "/#/login") only updates the fragment and does NOT reload
  // the page on its own. The explicit reload guarantees every in-memory cache
  // and module-level singleton (RTK Query, the axios interceptor counters,
  // analytics SDKs) is rebuilt from scratch.
  window.location.href = redirectTo;
  window.location.reload();
};
